import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import equals from 'ramda/src/equals';
import propEq from 'ramda/src/propEq';
import clone from 'ramda/src/clone';
import move from 'ramda/src/move';

import Button from '@components/@core/AnimatedButton';
import CheckBox from '@components/@core/CheckBox';
import EditDropdown from '@components/EditDropdown';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import Pagination from '@components/@core/Pagination';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import { inPage } from '~/services/util';
import { FEATURED_CONTENT_TYPE } from '~/services/constants';

const options = ['Add New', 'Edit', 'Archive', 'Move Up', 'Move Down'];

const Content = ({ onChange }) => {
    const dispatch = useDispatch();

    const features = useSelector((state) => state.community.featuredItems);
    const [current, setCurrent] = useState(1);
    const [search, setSearch] = useState('');
    const [featuredData, setFeaturedData] = useState(features);

    const handleSearch = (e) => {
        onChange({ search: e, page: 1, per_page: 10 });
        setSearch(e);
    };

    const handleAddFeatured = (item) => {
        dispatch(AppActions.modalRequest({ type: 'Add Featured', data: { before: { item } } }));
    };

    const handleOptionChange = (item) => (e) => {
        const index = findIndex((e) => equals(e, item), featuredData);

        let modules = clone(featuredData);
        switch (e) {
            case 'add new':
                handleAddFeatured();
                break;

            case 'edit':
                handleAddFeatured(item);
                break;

            case 'archive':
                const payload = {
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Confirm',
                            body: 'Are you sure you want to archive this item?',
                        },
                    },
                    callBack: {
                        onYes: () => dispatch(CommunityActions.postarchivefeatureditemRequest(item?.id)),
                    },
                };
                dispatch(AppActions.modalRequest(payload));
                break;

            case 'move up':
                modules = move(index, index - 1, modules);
                setFeaturedData(modules);
                break;

            case 'move down':
                modules = move(index, index + 1, modules);
                setFeaturedData(modules);
                break;

            default:
                break;
        }
    };

    return (
        <>
            <div className="d-flex flex-row card-bottom pt-3">
                <Input className="m-search" placeholder="Search here..." value={search} onChange={handleSearch} />
                <Button className="ml-4 m-search-btn" name="SEARCH" type="link" />
            </div>
            <div className="d-flex flex-row card-header pb-0">
                <p className="dsl-b22 bold me-auto mb-0">Featured Items</p>
                <Button name="+Add Featured" type="link" onClick={handleAddFeatured} />
            </div>
            {featuredData && featuredData.length > 0 ? (
                featuredData.map((item, index) => {
                    if (inPage(index, current, 10)) {
                        const contentType = find(propEq('type', item.content?.type), FEATURED_CONTENT_TYPE);
                        const itemClass = index % 10 === 0 ? 'card-bottom' : index % 10 === 9 || index === featuredData.length - 1 ? 'card-header' : 'card';
                        return (
                            <div key={`d${index}`} className={classNames('d-flex', itemClass)}>
                                <div className="d-h-between mb-3">
                                    <CheckBox title={contentType?.name || 'Unfeatured'} checked={item.status !== 0} />
                                    <EditDropdown options={options} onChange={handleOptionChange(item)} />
                                </div>
                                <p className="dsl-b18 bold">{item.data?.title}</p>
                                <div className="d-flex ssm-column">
                                    <div className="d-flex-4 pe-3">
                                        <Thumbnail size="responsive" source={item.data?.image} />
                                    </div>
                                    <div className="d-flex-5 px-3">
                                        <p className="dsl-b16">{item.data?.synopsis}</p>
                                        <span className="dsl-b14 word-break">{item.data?.url}</span>
                                    </div>
                                    <div className="d-flex-3 border-left px-3 mt-0 ssm-t-3 ms-0 ssm-l-3">
                                        <div className="d-flex">
                                            <p className="dsl-m12 w70">Views</p>
                                            <span className="dsl-b14">{item.stats?.views || 0}</span>
                                        </div>
                                        <div className="d-flex">
                                            <p className="dsl-m12 w70">Clicks</p>
                                            <span className="dsl-b14">{item.stats?.clicks || 0}</span>
                                        </div>
                                        <div className="d-flex">
                                            <p className="dsl-m12 w70">Start date</p>
                                            <span className="dsl-b14">{item.date_start ? moment(item.date_start).format('MMM DD, YY') : 'NA'}</span>
                                        </div>
                                        <div className="d-flex">
                                            <p className="dsl-m12 w70">End date</p>
                                            <span className="dsl-b14">{item.date_end ? moment(item.date_end).format('MMM DD, YY') : 'NA'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                })
            ) : (
                <p className="dsl-b16 no-content">No content</p>
            )}
            <Pagination className="card-content" current={current} total={Math.ceil(featuredData.length / 10)} per={10} pers={[]} onChange={(e) => setCurrent(e)} />
        </>
    );
};

Content.propTypes = {
    onChange: PropTypes.func,
};

Content.defaultProps = {
    onChange: () => {},
};

export default Content;
